import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2Icon,
  AlertCircleIcon,
  InfoIcon,
  XCircleIcon
} from 'lucide-react';

const toastTypes = {
  success: {
    icon: CheckCircle2Icon,
    bgColor: 'bg-custom-yellow',
    textColor: 'text-white-800',
    borderColor: 'border-black-200'
  },
  error: {
    icon: XCircleIcon,
    bgColor: 'bg-red-50',
    textColor: 'text-red-800',
    borderColor: 'border-red-200'
  },
  warning: {
    icon: AlertCircleIcon,
    bgColor: 'bg-yellow-50',
    textColor: 'text-yellow-800',
    borderColor: 'border-yellow-200'
  },
  info: {
    icon: InfoIcon,
    bgColor: 'bg-blue-50',
    textColor: 'text-blue-800',
    borderColor: 'border-blue-200'
  }
};

// ── Module-level singleton state ──
let toasts = [];
const listeners = new Set();

function notify() {
  listeners.forEach(fn => fn([...toasts]));
}

function globalAddToast(message, type = 'info', duration = 3000) {
  const id = Date.now() + Math.random();
  toasts = [...toasts, { id, message, type, duration }];
  notify();
  return id;
}

function globalRemoveToast(id) {
  toasts = toasts.filter(t => t.id !== id);
  notify();
}

// ── Single Toast item ──
const ToastItem = React.memo(({ id, message, type, duration }) => {
  const [isVisible, setIsVisible] = useState(true);
  const {
    icon: Icon,
    bgColor,
    textColor,
    borderColor
  } = toastTypes[type] || toastTypes.info;

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsVisible(false);
      globalRemoveToast(id);
    }, duration);
    return () => clearTimeout(timer);
  }, [id, duration]);

  if (!isVisible) return null;

  const displayMessage = typeof message === 'string' || typeof message === 'number'
    ? message
    : (message?.message || message?.code || JSON.stringify(message));

  return (
    <div
      className={`
        ${bgColor} ${textColor} ${borderColor}
        border rounded-lg shadow-lg
        flex items-center p-4
        transition-all duration-300 ease-in-out
        animate-slide-in
      `}
    >
      <Icon className="mr-3 h-6 w-6" />
      <div className="flex-grow">{displayMessage}</div>
      <button
        onClick={() => {
          setIsVisible(false);
          globalRemoveToast(id);
        }}
        className="ml-4 hover:bg-gray-100 rounded-full p-1"
      >
        <XCircleIcon className="h-5 w-5 opacity-50 hover:opacity-100" />
      </button>
    </div>
  );
});

// ── Global ToastContainer — render once, works everywhere ──
export const ToastContainer = () => {
  const [currentToasts, setCurrentToasts] = useState([]);

  useEffect(() => {
    listeners.add(setCurrentToasts);
    return () => listeners.delete(setCurrentToasts);
  }, []);

  if (currentToasts.length === 0) return null;

  return (
    <div className="fixed text-white top-4 right-4 z-[150] space-y-2">
      {currentToasts.map(toast => (
        <ToastItem
          key={toast.id}
          id={toast.id}
          message={toast.message}
          type={toast.type}
          duration={toast.duration}
        />
      ))}
    </div>
  );
};

// ── Hook — just returns addToast + a no-op ToastContainer for backwards compat ──
export const useToast = () => {
  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    return globalAddToast(message, type, duration);
  }, []);

  // Return a stable no-op component so existing <ToastContainer /> calls don't break
  const NoopContainer = useCallback(() => null, []);

  return { addToast, ToastContainer: NoopContainer };
};

export default ToastItem;
